import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GeometryObserver, QuillText, Tooltip, tokens } from '@treams/treams-ui';

const Bullet = styled(Flex)<{ filled?: boolean; crowded?: boolean; enabled?: boolean }>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	background: #dae0e8;
	overflow: hidden;

	${(props) => !props.filled && `transform: scale(0.25, 0.25);`}
	${(props) => props.crowded && `transform: rotateZ(45deg);`}
	${(props) => props.enabled === false && `opacity: 0.25;`}
`;

const Fill = styled.div<{ party: graphql.EvaluationSubmissionParty }>`
	width: 32px;
	height: 32px;
	background: ${(props) =>
		props.party === graphql.EvaluationSubmissionParty.Employee
			? props.theme.colors.employee
			: props.theme.colors.manager};
`;

const Dot = styled.div`
	position: absolute;
	left: 12px;
	top: 12px;
	width: 8px;
	height: 8px;
	background: white;
	border-radius: 8px;
`;

const Label = styled.div`
	color: ${tokens.text.secondary};
	text-overflow: ellipsis;
	overflow: hidden;
	flex-shrink: 0;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${tokens.border.subtle};
	margin-top: ${tokens.gap.element}px;
	padding-top: ${tokens.padding.compact}px;
	text-align: center;
`;

function CompetencyDisplayLevel(props: {
	variant: graphql.EvaluationSubmissionParty | 'both' | 'none';
	level: graphql.CompetencyLevelOption;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const isSelected = props.variant !== 'none';

	return (
		<Tooltip
			enabled={props.level.enabled}
			content={
				<>
					<QuillText
						value={localize(props.level.criteria ?? [])?.text}
						placeholder={t('evaluation.submission.competency.levels.noDescription')}
					/>
					<TooltipTitle>{localize(props.level.level.title)?.text}</TooltipTitle>
				</>
			}
		>
			<Box style={{ position: 'relative' }}>
				<Bullet filled={isSelected} enabled={props.level.enabled}>
					{[graphql.EvaluationSubmissionParty.Employee, 'both'].includes(props.variant) && (
						<Fill party={graphql.EvaluationSubmissionParty.Employee} />
					)}
					{[graphql.EvaluationSubmissionParty.Manager, 'both'].includes(props.variant) && (
						<Fill party={graphql.EvaluationSubmissionParty.Manager} />
					)}
					{isSelected && <Dot />}
				</Bullet>
			</Box>
		</Tooltip>
	);
}

export function CompetencyDisplayWithValue(props: {
	levels: graphql.CompetencyLevelOption[];
	party: graphql.EvaluationSubmissionParty | 'both';
	score: { manager?: number; employee?: number };
	labels?: boolean;
}) {
	const { localize } = useLocalize();
	const ref = useRef(null);

	// Use enabled levels for the left and right legend text.
	const enabledLevels = props.levels.filter((level) => !!level.enabled);

	const leftLabel = enabledLevels.length > 0 && props.labels !== false && (
		<Box mr={2}>
			<Label>{localize(enabledLevels[0].level.title).text}</Label>
		</Box>
	);

	const rightLabel = enabledLevels.length > 0 && props.labels !== false && (
		<Box ml={2}>
			<Label>{localize(enabledLevels[enabledLevels.length - 1].level.title).text}</Label>
		</Box>
	);

	const bullets = props.levels.map((currentLevel, i) => {
		// Filter bullets in this map, not before. This to preserve
		// the correct active index.
		if (!currentLevel.enabled) {
			return null;
		}

		const variant =
			props.score.employee === i && props.score.manager === i
				? 'both'
				: props.score.employee === i
					? graphql.EvaluationSubmissionParty.Employee
					: props.score.manager === i
						? graphql.EvaluationSubmissionParty.Manager
						: 'none';

		return <CompetencyDisplayLevel key={i} level={currentLevel} variant={variant} />;
	});

	return (
		<Box mb={4} ref={ref} width="100%">
			<GeometryObserver innerRef={ref}>
				{(bounds) =>
					bounds.width < 320 ? (
						<Box>
							<Flex alignItems="center" justifyContent="space-between">
								{bullets}
							</Flex>
							<Flex justifyContent={'space-between'}>
								{leftLabel}
								{rightLabel}
							</Flex>
						</Box>
					) : (
						<Flex width="100%" alignItems="center" justifyContent="space-between">
							{leftLabel}
							{bullets}
							{rightLabel}
						</Flex>
					)
				}
			</GeometryObserver>
		</Box>
	);
}

export default function CompetencyDisplay(props: {
	submission: graphql.EvaluationSubmission;
	section: number;
	item: number;
	party: graphql.EvaluationSubmissionParty | 'both';
}) {
	const { party } = props;
	const levels =
		props.submission.evaluationForm.sections[props.section].items[props.item].competency!.competency
			.levels;

	const employee =
		props.submission.employee.sections[props.section].items[props.item]?.competency ?? undefined;

	const manager =
		props.submission.manager.sections[props.section].items[props.item]?.competency ?? undefined;

	return (
		<CompetencyDisplayWithValue
			levels={levels}
			party={party}
			score={
				party === 'both'
					? { manager: manager, employee: employee }
					: party === graphql.EvaluationSubmissionParty.Employee
						? { employee }
						: party === graphql.EvaluationSubmissionParty.Manager
							? { manager }
							: {}
			}
		/>
	);
}
